import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Ban, CheckCircle2, X, Loader2, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import {
  adminListBans, adminCreateBan, adminRemoveBan,
  adminListClaims, adminApproveClaim, adminDenyClaim,
} from '../lib/api';

export const AdminBans = () => {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: '', phone: '', reason: '' });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const res = await adminListBans();
      setRows(res.data || []);
    } catch { toast.error('Could not load bans'); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.email && !form.phone) { toast.error('Email or phone required'); return; }
    if (!form.reason.trim()) { toast.error('Reason required'); return; }
    setBusy(true);
    try {
      await adminCreateBan(form);
      toast.success('User banned');
      setOpen(false);
      setForm({ email: '', phone: '', reason: '' });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Ban failed');
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Lift this ban?')) return;
    try {
      await adminRemoveBan(id);
      toast.success('Ban lifted');
      load();
    } catch { toast.error('Could not lift ban'); }
  };

  return (
    <div className="bg-[#18181B] border border-zinc-800 rounded-lg overflow-hidden" data-testid="admin-bans-tab">
      <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
        <p className="text-sm text-zinc-400">{rows.length} banned</p>
        <Button size="sm" onClick={() => setOpen(true)} className="bg-[#C0A062] text-black hover:bg-[#a98a52]" data-testid="add-ban-btn">
          <Ban className="w-4 h-4 mr-1" /> Add to ban list
        </Button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-[11px] uppercase tracking-wider text-zinc-500 bg-zinc-900/50">
            <tr>
              <th className="px-4 py-3">Identifier</th>
              <th className="px-4 py-3">Reason</th>
              <th className="px-4 py-3 hidden sm:table-cell">Banned at</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {rows.length === 0 ? (
              <tr><td colSpan={4} className="px-4 py-10 text-center text-zinc-500">No bans on file.</td></tr>
            ) : rows.map((b) => (
              <tr key={b.id} data-testid={`ban-row-${b.id}`} className="hover:bg-zinc-900/40">
                <td className="px-4 py-3 text-white">{b.email || b.phone || b.user_id}</td>
                <td className="px-4 py-3 text-zinc-400">{b.reason}</td>
                <td className="px-4 py-3 text-zinc-500 hidden sm:table-cell text-xs">{(b.created_at || '').slice(0, 10)}</td>
                <td className="px-4 py-3 text-right">
                  <Button size="sm" variant="ghost" className="text-emerald-300 hover:bg-emerald-500/10" onClick={() => remove(b.id)} data-testid={`unban-${b.id}`}>
                    Lift ban
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0d0d10] border-zinc-800 text-white max-w-md" data-testid="add-ban-dialog">
          <DialogHeader>
            <DialogTitle>Add to ban list</DialogTitle>
            <DialogDescription className="text-zinc-400">
              You can pre-ban by email or phone before the user signs up.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#09090B] border-zinc-700 text-white" data-testid="ban-email-input" />
            <Input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[#09090B] border-zinc-700 text-white" data-testid="ban-phone-input" />
            <Input placeholder="Reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="bg-[#09090B] border-zinc-700 text-white" data-testid="ban-reason-input" />
            <Button onClick={submit} disabled={busy} className="w-full bg-[#C0A062] text-black hover:bg-[#a98a52] font-semibold" data-testid="ban-submit-btn">
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Confirm ban
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const AdminClaims = () => {
  const [rows, setRows] = useState([]);
  const [viewing, setViewing] = useState(null);

  const load = async () => {
    try { const res = await adminListClaims(); setRows(res.data || []); }
    catch { toast.error('Could not load claims'); }
  };
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    try { await adminApproveClaim(id); toast.success('Claim approved'); load(); }
    catch { toast.error('Approval failed'); }
  };
  const deny = async (id) => {
    try { await adminDenyClaim(id); toast.success('Claim denied'); load(); }
    catch { toast.error('Denial failed'); }
  };

  return (
    <div className="bg-[#18181B] border border-zinc-800 rounded-lg overflow-hidden" data-testid="admin-claims-tab">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-[11px] uppercase tracking-wider text-zinc-500 bg-zinc-900/50">
            <tr>
              <th className="px-4 py-3">User</th>
              <th className="px-4 py-3">Action</th>
              <th className="px-4 py-3">Points</th>
              <th className="px-4 py-3">Submitted</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {rows.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-zinc-500">No pending claims.</td></tr>
            ) : rows.map((c) => (
              <tr key={c.id} data-testid={`claim-row-${c.id}`} className="hover:bg-zinc-900/40">
                <td className="px-4 py-3 text-white">{c.user_name || c.user_email}</td>
                <td className="px-4 py-3 text-zinc-300">{c.action.replace(/_/g, ' ')}</td>
                <td className="px-4 py-3 text-[#C0A062] font-semibold">+{c.points}</td>
                <td className="px-4 py-3 text-zinc-500 text-xs">{(c.created_at || '').slice(0, 10)}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" className="text-zinc-300 hover:bg-zinc-800" onClick={() => setViewing(c)} data-testid={`view-claim-${c.id}`}>
                    <ImageIcon className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" className="text-emerald-300 hover:bg-emerald-500/10" onClick={() => approve(c.id)} data-testid={`approve-claim-${c.id}`}>
                    <CheckCircle2 className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" className="text-amber-300 hover:bg-amber-500/10" onClick={() => deny(c.id)} data-testid={`deny-claim-${c.id}`}>
                    <X className="w-4 h-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!viewing} onOpenChange={() => setViewing(null)}>
        <DialogContent className="bg-[#0d0d10] border-zinc-800 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Screenshot review</DialogTitle>
            <DialogDescription className="text-zinc-400">
              {viewing?.user_email} · {viewing?.action?.replace(/_/g, ' ')} · +{viewing?.points} pts
            </DialogDescription>
          </DialogHeader>
          {viewing?.screenshot && (
            <img src={viewing.screenshot} alt="Claim screenshot" className="w-full max-h-[60vh] object-contain rounded border border-zinc-800" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
